import React, { useState } from "react";
import "./Workspace/workspace.css";
import { Modal, Button, Alert } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import {
  Clipboard,
  CheckCircle,
  Link45deg,
  ShieldExclamation,
  Globe,
  LockFill,
} from "react-bootstrap-icons";

interface ShareableReportModalProps {
  show: boolean;
  onHide: () => void;
  isShared: boolean;
  workproductId?: string;
  onToggleSharing: () => void;
  isLoading: boolean;
  shareUrl?: string; // Optional: pass custom share URL
}

const ShareableReportModal: React.FC<ShareableReportModalProps> = ({
  show,
  onHide,
  isShared,
  workproductId,
  onToggleSharing,
  isLoading,
  shareUrl: customShareUrl,
}) => {
  const { t } = useTranslation();
  const [copySuccess, setCopySuccess] = useState(false);

  const getShareUrl = () => {
    if (customShareUrl) return customShareUrl;
    if (!workproductId) return "";
    return `${window.location.origin}/shared/workproduct/${workproductId}`;
  };

  const handleCopyLink = async () => {
    if (!workproductId && !customShareUrl) return;

    const shareUrl = getShareUrl();
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 2500);
    } catch (err) {
      console.error("Failed to copy: ", err);
      // Fallback: select the text
      const textArea = document.querySelector(
        ".share-url-input"
      ) as HTMLInputElement;
      if (textArea) {
        textArea.select();
        document.execCommand("copy");
        setCopySuccess(true);
        setTimeout(() => setCopySuccess(false), 2500);
      }
    }
  };

  return (
    <Modal className="workspace-theme" show={show} onHide={onHide} centered style={{ zIndex: 1055 }}>
      <Modal.Header
        closeButton
        style={{
          background: "var(--ws-surface)",
          color: "var(--ws-ink)",
          borderBottom: "1px solid var(--ws-line)",
        }}
      >
        <Modal.Title className="d-flex align-items-center gap-2">
          {isShared ? (
            <>
              <Globe size={22} />
              {t("ShareModal.titleShared", "Public Link Active")}
            </>
          ) : (
            <>
              <LockFill size={20} />
              {t("ShareModal.titlePrivate", "Share This Work")}
            </>
          )}
        </Modal.Title>
      </Modal.Header>

      <Modal.Body style={{ padding: "24px" }}>
        {isShared ? (
          <>
            {/* Status indicator */}
            <div
              className="d-flex align-items-center gap-2 mb-3"
              style={{
                padding: "12px 16px",
                backgroundColor: "#e9f5fb",
                borderRadius: "8px",
                border: "1px solid var(--ws-teal)",
              }}
            >
              <Globe size={18} style={{ color: "var(--ws-teal)" }} />
              <span style={{ color: "var(--ws-teal)", fontWeight: 500 }}>
                {t(
                  "ShareModal.statusPublic",
                  "This work is publicly accessible"
                )}
              </span>
            </div>

            {/* Description */}
            <p style={{ color: "#495057", marginBottom: "16px" }}>
              {t("ShareModal.shareDescription")}
            </p>

            {/* URL Display */}
            <div style={{ marginBottom: "16px" }}>
              <label
                style={{
                  fontSize: "12px",
                  fontWeight: 600,
                  color: "#6c757d",
                  textTransform: "uppercase",
                  letterSpacing: "0.5px",
                  marginBottom: "8px",
                  display: "block",
                }}
              >
                {t("ShareModal.shareableLink", "Shareable Link")}
              </label>
              <div
                style={{
                  display: "flex",
                  alignItems: "stretch",
                  border: "2px solid #dee2e6",
                  borderRadius: "8px",
                  overflow: "hidden",
                  transition: "border-color 0.2s",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    padding: "0 12px",
                    backgroundColor: "#f8f9fa",
                    borderRight: "1px solid #dee2e6",
                  }}
                >
                  <Link45deg size={18} style={{ color: "var(--ws-teal)" }} />
                </div>
                <input
                  type="text"
                  className="share-url-input"
                  value={getShareUrl()}
                  readOnly
                  style={{
                    flex: 1,
                    border: "none",
                    padding: "12px",
                    fontSize: "14px",
                    color: "#333333",
                    backgroundColor: "white",
                    outline: "none",
                  }}
                  onClick={e => (e.target as HTMLInputElement).select()}
                />
                <button
                  onClick={handleCopyLink}
                  disabled={!workproductId && !customShareUrl}
                  style={{
                    border: "none",
                    borderRadius: 0,
                    padding: "12px 20px",
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                    fontWeight: 500,
                    minWidth: "100px",
                    justifyContent: "center",
                    cursor: "pointer",
                    background: copySuccess
                      ? "linear-gradient(135deg, #00a076, #008a65)"
                      : "linear-gradient(135deg, var(--ws-teal), #00a076)",
                    color: "#ffffff",
                    transition: "all 0.2s",
                  }}
                >
                  {copySuccess ? (
                    <>
                      <CheckCircle size={16} />
                      {t("ShareModal.copied", "Copied!")}
                    </>
                  ) : (
                    <>
                      <Clipboard size={16} />
                      {t("ShareModal.copy", "Copy")}
                    </>
                  )}
                </button>
              </div>
            </div>

            {/* Warning */}
            <Alert
              variant="warning"
              className="d-flex align-items-start gap-2 mb-0"
              style={{ fontSize: "14px" }}
            >
              <ShieldExclamation
                size={20}
                style={{ flexShrink: 0, marginTop: "2px" }}
              />
              <div>{t("ShareModal.shareWarning")}</div>
            </Alert>
          </>
        ) : (
          <>
            {/* Not shared state */}
            <div
              className="text-center"
              style={{ padding: "20px 0", marginBottom: "16px" }}
            >
              <div
                style={{
                  width: "64px",
                  height: "64px",
                  borderRadius: "50%",
                  backgroundColor: "#f8f9fa",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  margin: "0 auto 16px",
                }}
              >
                <LockFill size={28} style={{ color: "#6c757d" }} />
              </div>
              <h5 style={{ marginBottom: "8px", color: "#333333" }}>
                {t("ShareModal.privateTitle", "This work is private")}
              </h5>
              <p style={{ color: "#666666", marginBottom: 0 }}>
                {t("ShareModal.enableSharingPrompt")}
              </p>
            </div>

            {/* Warning about enabling */}
            <Alert
              variant="info"
              className="d-flex align-items-start gap-2 mb-0"
              style={{ fontSize: "14px" }}
            >
              <ShieldExclamation
                size={20}
                style={{ flexShrink: 0, marginTop: "2px" }}
              />
              <div>
                {t(
                  "ShareModal.enableWarning",
                  "When you enable sharing, anyone with the link will be able to view this work without logging in. Make sure you're comfortable sharing this content publicly."
                )}
              </div>
            </Alert>
          </>
        )}
      </Modal.Body>

      <Modal.Footer
        style={{
          borderTop: "1px solid #dee2e6",
          padding: "16px 24px",
          justifyContent: "space-between",
        }}
      >
        <Button
          variant="outline-secondary"
          onClick={onHide}
          style={{ minWidth: "100px" }}
        >
          {t("ShareModal.close")}
        </Button>
        <button
          onClick={onToggleSharing}
          disabled={isLoading}
          style={{
            minWidth: "160px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: "8px",
            padding: "8px 16px",
            border: isShared ? "1px solid #dc3545" : "none",
            borderRadius: "6px",
            fontWeight: 500,
            cursor: isLoading ? "not-allowed" : "pointer",
            background: isShared
              ? "#ffffff"
              : "linear-gradient(135deg, var(--ws-teal), #00a076)",
            color: isShared ? "#dc3545" : "#ffffff",
            opacity: isLoading ? 0.65 : 1,
            transition: "all 0.2s",
          }}
        >
          {isLoading ? (
            <>
              <span
                className="spinner-border spinner-border-sm"
                role="status"
                aria-hidden="true"
              ></span>
              {t("ShareModal.processing")}
            </>
          ) : isShared ? (
            <>
              <LockFill size={14} />
              {t("ShareModal.disableSharing")}
            </>
          ) : (
            <>
              <Globe size={14} />
              {t("ShareModal.enableSharing")}
            </>
          )}
        </button>
      </Modal.Footer>
    </Modal>
  );
};

export default ShareableReportModal;
